import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiService } from '../services/apiService';
import { API, UpdateAPIRequest } from '../types/api';
import { ReadmeUpload } from './ReadmeUpload';
import './EnhancedAddAPI.css';

interface EditAPIState {
  formData: {
    name: string;
    description: string;
    url: string;
    status: 'active' | 'inactive' | 'deprecated' | 'deleted';
    version: string;
    documentation: string;
    authentication: string;
    rateLimit: string;
    payloadDescription: string;
    extractedFromReadme: boolean;
    readmeAnalysisConfidence?: {
      name: number;
      description: number;
      url: number;
      tags: number;
      payloadDescription: number;
    };
    originalReadmeContent?: string;
  };
  tags: string[];
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string | null;
  showReadmeUpload: boolean;
}

export const EditAPI: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: api, loading, error, execute } = useApi<API>();

  const [state, setState] = useState<EditAPIState>({
    formData: {
      name: '',
      description: '',
      url: '',
      status: 'active',
      version: '',
      documentation: '',
      authentication: '',
      rateLimit: '',
      payloadDescription: '',
      extractedFromReadme: false,
    },
    tags: [],
    isSubmitting: false,
    submitSuccess: false,
    submitError: null,
    showReadmeUpload: false,
  });

  // Load API data when component mounts
  useEffect(() => {
    if (id) {
      execute(() => apiService.getAPI(id));
    }
  }, [id, execute]);

  // Populate form when API data is loaded
  useEffect(() => {
    if (api) {
      setState(prev => ({
        ...prev,
        formData: {
          name: api.name || '',
          description: api.description || '',
          url: api.url || '',
          status: api.status || 'active',
          version: api.version || '',
          documentation: api.documentation || '',
          authentication: api.authentication || '',
          rateLimit: api.rateLimit || '',
          payloadDescription: (api as any).payloadDescription || '',
          extractedFromReadme: (api as any).extractedFromReadme || false,
          readmeAnalysisConfidence: (api as any).readmeAnalysisConfidence,
          originalReadmeContent: (api as any).originalReadmeContent,
        },
        tags: api.tags || [],
      }));
    }
  }, [api]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [name]: value,
      },
    }));
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    setState(prev => ({
      ...prev,
      tags: tagsArray,
    }));
  };

  const handleReadmeAnalysis = (analysisResult: any) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        name: analysisResult.name || prev.formData.name,
        description: analysisResult.description || prev.formData.description,
        url: analysisResult.url || prev.formData.url,
        payloadDescription: analysisResult.payloadDescription || prev.formData.payloadDescription,
        extractedFromReadme: true,
        readmeAnalysisConfidence: analysisResult.confidence,
        originalReadmeContent: analysisResult.originalContent,
      },
      tags: analysisResult.tags || prev.tags,
      showReadmeUpload: false,
    }));
  };

  const handleReadmeError = (error: string) => {
    setState(prev => ({
      ...prev,
      submitError: `README analysis failed: ${error}`,
      showReadmeUpload: false,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) {
      setState(prev => ({ ...prev, submitError: 'API ID is missing' }));
      return;
    }

    setState(prev => ({ 
      ...prev, 
      isSubmitting: true, 
      submitError: null,
      submitSuccess: false 
    }));

    try {
      const updateData: UpdateAPIRequest = {
        ...state.formData,
        tags: state.tags,
        updatedBy: 'current-user@example.com', // This would come from auth context
      };

      await apiService.updateAPI(id, updateData);
      
      setState(prev => ({ 
        ...prev, 
        submitSuccess: true, 
        isSubmitting: false 
      }));

      // Navigate back to API details page after a short delay
      setTimeout(() => {
        navigate(`/api/${id}`);
      }, 1500);

    } catch (error) {
      setState(prev => ({
        ...prev,
        submitError: error instanceof Error ? error.message : 'Failed to update API',
        isSubmitting: false
      }));
    }
  };

  if (loading) {
    return (
      <div className="enhanced-add-api">
        <div className="container">
          <div className="loading">Loading API data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enhanced-add-api">
        <div className="container">
          <div className="error">Error loading API: {String(error)}</div>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!api) {
    return (
      <div className="enhanced-add-api">
        <div className="container">
          <div className="error">API not found</div>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-add-api">
      <div className="container">
        <div className="header-section">
          <h2>Edit API</h2>
          <p>Update the API information below</p>
          
          <div className="action-buttons">
            <button
              type="button"
              onClick={() => navigate(`/api/${id}`)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setState(prev => ({ ...prev, showReadmeUpload: !prev.showReadmeUpload }))}
              className="btn-secondary"
            >
              {state.showReadmeUpload ? 'Hide' : 'Update from'} README
            </button>
          </div>
        </div>

        {state.showReadmeUpload && (
          <div className="readme-upload-section">
            <ReadmeUpload 
              onAnalysisComplete={handleReadmeAnalysis} 
              onError={handleReadmeError}
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="api-form">
          <div className="form-group">
            <label htmlFor="name">API Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={state.formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter API name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={state.formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              placeholder="Describe what this API does"
            />
          </div>

          <div className="form-group">
            <label htmlFor="url">API URL *</label>
            <input
              type="url"
              id="url"
              name="url"
              value={state.formData.url}
              onChange={handleInputChange}
              required
              placeholder="https://api.example.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={state.formData.status}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deprecated">Deprecated</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="version">Version</label>
              <input
                type="text"
                id="version"
                name="version"
                value={state.formData.version}
                onChange={handleInputChange}
                placeholder="1.0.0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={state.tags.join(', ')}
              onChange={handleTagsChange}
              placeholder="api, rest, json (comma-separated)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="documentation">Documentation URL</label>
            <input
              type="url"
              id="documentation"
              name="documentation"
              value={state.formData.documentation}
              onChange={handleInputChange}
              placeholder="https://docs.example.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="authentication">Authentication</label>
              <input
                type="text"
                id="authentication"
                name="authentication"
                value={state.formData.authentication}
                onChange={handleInputChange}
                placeholder="Bearer Token, API Key, etc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="rateLimit">Rate Limit</label>
              <input
                type="text"
                id="rateLimit"
                name="rateLimit"
                value={state.formData.rateLimit}
                onChange={handleInputChange}
                placeholder="100 requests/minute"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="payloadDescription">Payload Description</label>
            <textarea
              id="payloadDescription"
              name="payloadDescription"
              value={state.formData.payloadDescription}
              onChange={handleInputChange}
              rows={4}
              placeholder="Describe the request/response format, endpoints, and data structures"
            />
          </div>

          {state.formData.extractedFromReadme && (
            <div className="readme-info">
              <h4>README Analysis Information</h4>
              <p>This API was populated from README analysis with the following confidence scores:</p>
              {state.formData.readmeAnalysisConfidence && (
                <ul>
                  <li>Name: {(state.formData.readmeAnalysisConfidence.name * 100).toFixed(0)}%</li>
                  <li>Description: {(state.formData.readmeAnalysisConfidence.description * 100).toFixed(0)}%</li>
                  <li>URL: {(state.formData.readmeAnalysisConfidence.url * 100).toFixed(0)}%</li>
                  <li>Tags: {(state.formData.readmeAnalysisConfidence.tags * 100).toFixed(0)}%</li>
                  <li>Payload: {(state.formData.readmeAnalysisConfidence.payloadDescription * 100).toFixed(0)}%</li>
                </ul>
              )}
            </div>
          )}

          {state.submitError && (
            <div className="error-message">
              {state.submitError}
            </div>
          )}

          {state.submitSuccess && (
            <div className="success-message">
              API updated successfully! Redirecting to details page...
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(`/api/${id}`)}
              className="btn-secondary"
              disabled={state.isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={state.isSubmitting}
            >
              {state.isSubmitting ? 'Updating...' : 'Update API'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAPI;
